import { LOCAL } from "@mn/shared";
import { useEffect, useState } from "react";
import * as Y from "yjs";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useDeepVersion } from "../hooks";
import { useBoardUI } from "../store";

interface Msg {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: number;
  editedAt?: number;
}
interface Person {
  id: string;
  name: string;
}

const timeAgo = (ts: number, locale: string) => {
  const d = Date.now() - ts;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (d < 60e3) return rtf.format(0, "minute");
  if (d < 3600e3) return rtf.format(-Math.round(d / 60e3), "minute");
  if (d < 86400e3) return rtf.format(-Math.round(d / 3600e3), "hour");
  return rtf.format(-Math.round(d / 86400e3), "day");
};

function highlight(text: string) {
  const parts = text.split(/(@[\p{L}\p{N}_][\p{L}\p{N}_ ]{0,40}?)(?=\s|$|[.,;:!?])/u);
  return parts.map((p, i) =>
    p.startsWith("@") ? (
      <span key={`${i}-${p}`} className="rounded-sm bg-[#8aa9ff]/25 px-0.5 text-text">
        {p}
      </span>
    ) : (
      p
    ),
  );
}

/** Comment thread card: messages live in a Y.Array so replies from several people merge cleanly. */
export function CommentBody({
  id,
  ymap,
  collapsed,
}: {
  id: string;
  ymap: Y.Map<unknown>;
  collapsed?: boolean;
}) {
  const { handle, user, role, boardId } = useBoard();
  const t = useT();
  const msgs = ymap.get("messages") as Y.Array<Y.Map<unknown>>;
  useDeepVersion(msgs);
  const [draft, setDraft] = useState("");
  const [people, setPeople] = useState<Person[]>([]);
  const [mentionQ, setMentionQ] = useState<string | null>(null);
  const selected = useBoardUI((s) => s.selection.has(id));
  const canComment = role !== "viewer" && role !== "none";
  const list: Msg[] = msgs.toArray().map((m) => m.toJSON() as Msg);
  const tx = (fn: () => void) => handle.doc.transact(fn, LOCAL);

  useEffect(() => {
    if (!canComment) return;
    api<Person[]>(`/api/boards/${boardId}/people`)
      .then(setPeople)
      .catch(() => {});
  }, [boardId, canComment]);
  const suggestions =
    mentionQ !== null
      ? people.filter((p) => p.name.toLowerCase().includes(mentionQ.toLowerCase())).slice(0, 6)
      : [];
  const insertMention = (p: Person) => {
    setDraft((d) => d.replace(/@[^\s@]*$/, `@${p.name} `));
    setMentionQ(null);
  };

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    const m = new Y.Map<unknown>();
    tx(() => {
      msgs.push([m]);
      m.set("id", crypto.randomUUID());
      m.set("authorId", user.id);
      m.set("authorName", user.name);
      m.set("body", body);
      m.set("createdAt", Date.now());
    });
    setDraft("");
    setMentionQ(null);
    handle.provider.setAwarenessField("typingIn", null);
    // Let the server notify mentioned people and other participants (and log activity).
    const mentions = people.filter((p) => body.includes(`@${p.name}`)).map((p) => p.id);
    const participants = [...new Set(list.map((x) => x.authorId))];
    api(`/api/boards/${boardId}/events`, {
      method: "POST",
      json: { type: "comment", cardId: id, text: body, mentions, participants },
    }).catch(() => {});
  };

  if (collapsed)
    return (
      <button
        type="button"
        onClick={() => tx(() => ymap.set("collapsed", false))}
        onPointerDown={(e) => e.stopPropagation()}
        className="flex items-center gap-1 px-2 py-1 text-[12px]"
        title={list[0]?.body}
      >
        💬 <span className="font-semibold">{list.length}</span>
      </button>
    );

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-3 pt-2 text-[11px] text-dim">
        <span>{list[0] ? t.commentStarted(list[0].authorName) : t.cardTypes.comment}</span>
        <button
          type="button"
          onClick={() => tx(() => ymap.set("collapsed", true))}
          onPointerDown={(e) => e.stopPropagation()}
          className="hover:text-text"
          aria-label="−"
        >
          −
        </button>
      </div>
      <ul
        className="max-h-72 overflow-y-auto px-3 py-2"
        onPointerDown={(e) => selected && e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        {list.map((m) => (
          <li key={m.id} className="mb-2 text-[13px]">
            <div className="flex items-baseline gap-2">
              <span className="font-semibold">{m.authorName}</span>
              <span className="text-[10px] text-dim">
                {timeAgo(m.createdAt, document.documentElement.lang || "es")}
              </span>
            </div>
            <div className="whitespace-pre-wrap break-words text-muted">{highlight(m.body)}</div>
          </li>
        ))}
      </ul>
      {canComment && suggestions.length ? (
        <ul
          className="mx-3 mb-1 rounded-sm border border-border bg-panel-2 text-[13px]"
          onPointerDown={(e) => e.stopPropagation()}
        >
          {suggestions.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertMention(p)}
                className="block w-full px-2 py-1 text-left hover:bg-panel-3"
              >
                @{p.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {canComment ? (
        <div
          className="flex items-end gap-2 border-t border-border px-3 py-2"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              const m = /@([^\s@]*)$/.exec(e.target.value);
              setMentionQ(m ? (m[1] ?? "") : null);
            }}
            onFocus={() => handle.provider.setAwarenessField("typingIn", id)}
            onBlur={() => handle.provider.setAwarenessField("typingIn", null)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (suggestions.length && mentionQ !== null) insertMention(suggestions[0]!);
                else send();
              }
              if (e.key === "Escape") setMentionQ(null);
            }}
            rows={1}
            placeholder={list.length ? t.reply : t.writeComment}
            title={t.mentionHint}
            className="min-h-8 flex-1 resize-none bg-transparent text-[13px] outline-none placeholder:text-dim"
          />
          <button
            type="button"
            onClick={send}
            disabled={!draft.trim()}
            className="rounded-sm bg-accent px-2 py-1 text-[12px] font-semibold text-bg disabled:opacity-40"
          >
            {t.send}
          </button>
        </div>
      ) : null}
    </div>
  );
}
